'use client';

import Link from 'next/link';
import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, TextField, Chip, Stack, Button, IconButton, Link as MuiLink } from '@mui/material';
import ExpandMoreIcon from '@/letsgo/icons/ExpandMore';
import { useState } from 'react';
import ThumbsUpIcon from '@/letsgo/icons/ThumbUpAltOutlined';
import ThumbsDownIcon from '@/letsgo/icons/ThumbDownAltOutlined';
import ContactSupportIcon from '@/letsgo/icons/ContactSupport';
import { SUPPORT_EMAIL, REFUND_POLICY_USER_SUMMARY } from '@/lib/site-contact';

const FAQs = [
  // FAQs from Pricing Page
  {
    id: 'pricing-faq-1',
    question: 'What is the difference between Creator and Pro plans?',
    answer: 'The Creator plan offers 3 social accounts and basic scheduling, while the Pro plan includes all social accounts, advanced scheduling, premium template marketplace, and early access to new features.',
    category: 'Pricing & Plans',
    popular: true,
  },
  {
    id: 'pricing-faq-2',
    question: 'Can I change my plan later?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time directly from your account settings. Changes will take effect at the beginning of your next billing cycle.',
    category: 'Pricing & Plans',
    popular: false,
  },
  {
    id: 'pricing-faq-3',
    question: 'Do you offer refunds?',
    answer: `${REFUND_POLICY_USER_SUMMARY} Refunds are not issued from inside the app: email ${SUPPORT_EMAIL} or use the Contact page with subject “Billing or refund request.” Include the email address on your FloAI account and whether you want a refund, a subscription canceled, or both. We typically respond within 1–2 business days; processing time on your card may take a few extra days after we approve an eligible refund.`,
    category: 'Billing & Refunds',
    popular: false,
  },
  {
    id: 'pricing-faq-4',
    question: 'What are White-Label options?',
    answer: 'White-Label options allow you to rebrand FloAI Studio as your own platform, perfect for agencies or businesses looking to offer a branded solution to their clients. This includes custom domains, branding, and dedicated infrastructure.',
    category: 'Pricing & Plans',
    popular: true,
  },
  {
    id: 'pricing-faq-5',
    question: 'How does billing work for annual plans?',
    answer: 'When you choose an annual plan, you are billed once upfront for the entire year, typically at a discounted rate compared to monthly payments. Your subscription will automatically renew annually unless canceled.',
    category: 'Billing & Refunds',
    popular: false,
  },
  // FAQs from Support Page
  {
    id: 'support-faq-1',
    question: 'How do I connect my social media accounts to FloAI?',
    answer: 'To connect your social media accounts, navigate to the &apos;Settings&apos; section in your dashboard, then select &apos;Connected Accounts&apos;. From there, you can choose the platforms you wish to integrate and follow the on-screen prompts for authorization. FloAI currently supports integration with Facebook, Instagram, Twitter/X, and more.',
    category: 'Getting Started',
    popular: true,
  },
  {
    id: 'support-faq-2',
    question: 'What types of content can I generate with FloAI&apos;s AI Content Generator?',
    answer: 'FloAI&apos;s AI Content Generator can help you create a wide range of content, including social media captions, blog post ideas, email drafts, ad copy, and more. You can customize prompts to tailor the output to your specific needs and brand voice.',
    category: 'Features',
    popular: true,
  },
  {
    id: 'support-faq-3',
    question: 'How do I schedule posts using the Content Calendar?',
    answer: 'The Content Calendar allows you to plan and schedule your posts effortlessly. Simply go to the &apos;Content Calendar&apos; in your dashboard, select a date, and click to create a new event. You can then draft your content, select target platforms, and set your preferred publishing time. FloAI will handle the rest!',
    category: 'Features',
    popular: false,
  },
  {
    id: 'support-faq-4',
    question: 'Where can I find analytics for my content performance?',
    answer: 'Detailed analytics for your content performance are available in the &apos;Analytics & Insights&apos; section of your dashboard. Here, you can view overall performance, platform breakdowns, top-performing content, and audience insights to optimize your strategy.',
    category: 'Analytics',
    popular: false,
  },
  {
    id: 'support-faq-5',
    question: 'Is there a mobile app for FloAI?',
    answer: 'FloAI offers an optimized mobile experience accessible directly through your web browser on any device. While a dedicated mobile app is not available at this moment, our mobile-responsive design ensures you can manage your content and engage with your audience on the go.',
    category: 'General',
    popular: false,
  }
];

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedCategory, setSelectedCategory] = useState<string | 'All'>(`All`); // State for selected category

  const categories = [
    'All',
    ...Array.from(new Set(FAQs.map(faq => faq.category)))
  ];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const filteredFAQs = FAQs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PublicPageLayout>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} mb={3} color="text.primary">
          Frequently Asked Questions
        </Typography>
        <Typography variant="h6" color="text.secondary" mb={5}>
          Find quick answers to your questions about FloAI Studio.
        </Typography>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
          {/* Search Bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 4, bgcolor: 'background.paper', borderRadius: 1 }}
          />

          {/* Category Filters */}
          <Stack direction="row" spacing={1} overflow="auto" py={1} mb={4} sx={{ justifyContent: { xs: 'flex-start', sm: 'center' }, px: 2, flexWrap: 'nowrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', minWidth: 'fit-content' }}
              />
            ))}
          </Stack>

          {/* Most Popular FAQs Section */}
          {searchTerm === '' && selectedCategory === 'All' && (
            <Box sx={{ mb: 5, textAlign: 'left' }}>
              <Typography variant="h5" fontWeight={700} mb={3} color="text.primary">
                Most Popular Questions
              </Typography>
              {
                FAQs.filter(faq => faq.popular).map((faq) => (
                  <Accordion
                    key={faq.id}
                    expanded={expanded === `popular-${faq.id}`}
                    onChange={handleAccordionChange(`popular-${faq.id}`)}
                    sx={{ mb: 1, boxShadow: 1, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`popular-${faq.id}-content`}
                      id={`popular-${faq.id}-header`}
                      sx={{ bgcolor: 'action.hover' }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Was this helpful?</Typography>
                        <IconButton size="small" color="primary" aria-label="Thumbs Up"><ThumbsUpIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" aria-label="Thumbs Down"><ThumbsDownIcon fontSize="small" /></IconButton>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              }
            </Box>
          )}

          {/* FAQ Section */}
          <Box sx={{ mb: 5 }}>
            {filteredFAQs.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={handleAccordionChange(faq.id)}
                sx={{ mb: 1, boxShadow: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${faq.id}-content`}
                  id={`${faq.id}-header`}
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Was this helpful?</Typography>
                    <IconButton size="small" color="primary" aria-label="Thumbs Up"><ThumbsUpIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" aria-label="Thumbs Down"><ThumbsDownIcon fontSize="small" /></IconButton>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
            {filteredFAQs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No FAQs found matching your search or selected category.
              </Typography>
            )}
          </Box>

          <Typography variant="h5" color="text.primary" sx={{ mt: 5, mb: 3 }} fontWeight={700}>
            Still Need Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            New here? See our <MuiLink component={Link} href="/getting-started" color="primary" sx={{ fontWeight: 600 }}>Getting Started</MuiLink> guide.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Our dedicated support team is here to assist you.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="/contact"
            startIcon={<ContactSupportIcon />}
            sx={{ py: 1.5, px: 4, fontSize: '1.1rem' }}
          >
            Contact Our Support Team
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can also email us directly at{' '}
            <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary.main">
              {SUPPORT_EMAIL}
            </MuiLink>
          </Typography>

        </Box>
      </Box>
    </PublicPageLayout>
  );
};

export default FAQPage;
